<template>
  <div ref="modal" class="modal fade background-darken" :class="{ in: isOpen, show: isShow }" :style="{ display: isShow ? 'block' : 'none' }" tabindex="-1" role="dialog" @click.self="close" @keyup.esc="close">
    <div class="modal-dialog" :class="modalSize" :style="modalStyle" role="document">
      <div class="modal-content">
        <div v-if="needHeader" class="modal-header">
          <button type="button" class="close" aria-label="Close" @click="close">
            <span aria-hidden="true">&times;</span>
          </button>
          <h4 class="modal-title">
            <slot name="title">Modal</slot>
          </h4>
        </div>
        <div class="modal-body">
          <slot name="body">Body</slot>
        </div>
        <div v-if="needFooter" class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    opened     : { type: Function, default: ()=> {} },
    closed     : { type: Function, default: ()=> {} },
    needHeader : { type: Boolean         , default: true },
    needFooter : { type: Boolean         , default: true },
    size       : { type: [String, Number], default: ''   },
  },

  data() {
    return {
      isOpen       : false,
      isShow       : false,
      lastOverflow : 'auto',
      sizeClasses  : {
        sm: 'modal-sm', small : 'modal-sm',
        md: 'modal-md', medium: 'modal-md',
        lg: 'modal-lg', large : 'modal-lg',
        xlg: 'modal-xlg',
        full: 'modal-full',
      },
    };
  },

  computed: {
    // A named size resolves to a class; anything else is treated as a width.
    modalSize() {
      return this.sizeClasses[this.size] || '';
    },

    // Numeric (667 / "667") or CSS (`"667px"`, `"80%"`) sizes set an explicit max-width.
    modalStyle() {
      if (this.modalSize || this.size === '' || this.size == null) return {};

      const width = /^\d+$/.test(String(this.size)) ? `${this.size}px` : String(this.size);
      return { maxWidth: width };
    },
  },

  methods: {
    open() {
      if (this.isShow) return;
      this.isShow = true;
      this.$nextTick(()=> {
        this.isOpen = true;
        this.$refs.modal?.focus();
        this.lastOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.opened();
      });
    },

    close() {
      this.isOpen = false;
      this.$nextTick(()=> {
        setTimeout(()=> {
          this.isShow = false;
          document.body.style.overflow = this.lastOverflow;
          this.closed();
        }, 500);
      });
    },
  },
};
</script>

<style scoped>

.modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px 16px;
  opacity: 0;
  transition: opacity .25s ease;
  -webkit-overflow-scrolling: touch;
}

.background-darken {
  background: rgba(10, 22, 18, 0.55);
  backdrop-filter: blur(2px);
}

.modal.show {
  opacity: 1;
}

.modal-dialog {
  max-width: 560px;
  margin: 24px auto;
  transform: translateY(-16px);
  transition: transform .25s ease;
}

.modal.in .modal-dialog {
  transform: translateY(0);
}

.modal-xlg { max-width: 1040px; }
.modal-lg { max-width: 820px; }
.modal-md { max-width: 640px; }
.modal-sm { max-width: 420px; }

.modal-full {
  max-width: none;
  margin: 16px;
}

.modal-content {
  position: relative;
  background: var(--paper, #fff);
  border: 1px solid var(--line, #E5E7E2);
  border-radius: var(--r-lg, 16px);
  box-shadow: 0 24px 60px rgba(10, 22, 18, 0.22);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--line, #E5E7E2);
}

.modal-title {
  flex: 1;
  margin: 0;
  order: 1;
  font-family: var(--display, sans-serif);
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink, #0A1612);
}

.close {
  order: 2;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: var(--bg, #f5f6f3);
  color: var(--muted, #6B7770);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: background .15s ease, color .15s ease;
}

.close:hover {
  background: var(--line, #E5E7E2);
  color: var(--ink, #0A1612);
}

.modal-body {
  padding: 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  color: var(--ink-2, #4a5568);
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--line, #E5E7E2);
}

@media (max-width: 600px) {
  .modal-dialog { margin: 0 auto; }
  .modal-body { max-height: none; }
}

</style>
